import type { ParametersLocation } from "@/lib/api";

export type StationLatLng = {
  latitude: number;
  longitude: number;
};

/**
 * Degrees: ~11 m at the equator. Tight enough that a hand-edited pin stays
 * put, loose enough that float/input rounding still counts as “the station”.
 */
const COORDINATE_MATCH_EPSILON_DEGREES = 1e-4;

function parseCoordinate(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function coordinatesUnset(latitude: unknown, longitude: unknown): boolean {
  return (
    parseCoordinate(latitude) === undefined && parseCoordinate(longitude) === undefined
  );
}

function coordinatesMatchStation(
  station: StationLatLng,
  latitude: unknown,
  longitude: unknown,
): boolean {
  const currentLatitude = parseCoordinate(latitude);
  const currentLongitude = parseCoordinate(longitude);
  if (currentLatitude === undefined || currentLongitude === undefined) return false;
  return (
    Math.abs(currentLatitude - station.latitude) <= COORDINATE_MATCH_EPSILON_DEGREES &&
    Math.abs(currentLongitude - station.longitude) <= COORDINATE_MATCH_EPSILON_DEGREES
  );
}

/** Reads catalogue station coordinates when both values are finite. */
export function stationCoordinates(
  station: ParametersLocation | undefined,
): StationLatLng | undefined {
  const latitude = station?.coordinates?.latitude;
  const longitude = station?.coordinates?.longitude;
  // `Number.isFinite` does not narrow, so check the type explicitly.
  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return undefined;
  }
  return { latitude, longitude };
}

/**
 * When the linked observed station changes, decide whether to copy that
 * station’s lat/lng onto the dashboard location:
 * - copy if lat/lng are empty
 * - copy if they still match the previously linked station (within epsilon)
 * - otherwise leave the current values (they were customized)
 */
export function resolveCoordinatesAfterLinkedStationChange({
  currentLatitude,
  currentLongitude,
  previousStation,
  nextStation,
}: {
  currentLatitude: unknown;
  currentLongitude: unknown;
  previousStation: StationLatLng | undefined;
  nextStation: StationLatLng | undefined;
}): StationLatLng | undefined {
  if (!nextStation) return undefined;
  if (coordinatesUnset(currentLatitude, currentLongitude)) return nextStation;
  if (
    previousStation &&
    coordinatesMatchStation(previousStation, currentLatitude, currentLongitude)
  ) {
    return nextStation;
  }
  return undefined;
}
